import { z } from "zod";
import {
    ConsolidationEntityRelationshipType,
    ConsolidationGroupStatus,
    ConsolidationTreatment,
} from "@common/types/domains/accounting/consolidation";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";

/**
 * Reference-only descriptive fields (never an accounting source of truth).
 * Type-specific free-text fields per the ownership canvas designs:
 * name (all), ownershipType (ownership), payment (rent/loan),
 * targetBalance + frequency (cash sweep).
 */
export const consolidationRelationshipMetadataSchema = z.object({
    name: z.string().max(200).optional(),
    roleLabel: z.string().max(200).optional(),
    notes: z.string().max(2000).optional(),
    ownershipType: z.string().max(100).optional(),
    payment: z.string().max(100).optional(),
    targetBalance: z.string().max(100).optional(),
    frequency: z.string().max(100).optional(),
});

export const consolidationRelationshipSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    type: z.nativeEnum(ConsolidationEntityRelationshipType),
    sourceEntityUuid: z.string().uuid(),
    targetEntityUuid: z.string().uuid(),
    ownershipPercentage: z.number().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    metadata: consolidationRelationshipMetadataSchema.nullable(),
});

export type ConsolidationRelationshipResponse = z.infer<
    typeof consolidationRelationshipSchema
>;

const ownershipPercentageSchema = z.number().gt(0).max(100).multipleOf(0.01, {
    message: "Ownership percentage supports at most two decimal places",
});

interface RelationshipInvariants {
    type?: ConsolidationEntityRelationshipType;
    ownershipPercentage?: number | null;
    startDate?: string | null;
    endDate?: string | null;
}

function validateRelationshipInvariants(
    value: RelationshipInvariants,
    ctx: z.RefinementCtx,
): void {
    const isOwnership =
        value.type === ConsolidationEntityRelationshipType.ownership;
    const hasPercentage =
        value.ownershipPercentage !== undefined &&
        value.ownershipPercentage !== null;
    if (value.type !== undefined && isOwnership && !hasPercentage) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ownershipPercentage"],
            message: "Ownership relationships require an ownership percentage",
        });
    }
    if (value.type !== undefined && !isOwnership && hasPercentage) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ownershipPercentage"],
            message:
                "Ownership percentage is only allowed on ownership relationships",
        });
    }
    validateDateOrder(value, ctx);
}

function validateDateOrder(
    value: Pick<RelationshipInvariants, "startDate" | "endDate">,
    ctx: z.RefinementCtx,
): void {
    if (value.startDate && value.endDate && value.endDate < value.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message: "End date must not be before start date",
        });
    }
}

export const createConsolidationRelationshipBodySchema = z
    .object({
        type: z.nativeEnum(ConsolidationEntityRelationshipType),
        sourceEntityUuid: z.string().uuid(),
        targetEntityUuid: z.string().uuid(),
        ownershipPercentage: ownershipPercentageSchema.optional(),
        startDate: z.string().date().optional(),
        endDate: z.string().date().optional(),
        metadata: consolidationRelationshipMetadataSchema.optional(),
    })
    .superRefine(validateRelationshipInvariants);

export type CreateConsolidationRelationshipBody = z.infer<
    typeof createConsolidationRelationshipBodySchema
>;

export const updateConsolidationRelationshipBodySchema = z
    .object({
        ownershipPercentage: ownershipPercentageSchema.nullable().optional(),
        startDate: z.string().date().nullable().optional(),
        endDate: z.string().date().nullable().optional(),
        metadata: consolidationRelationshipMetadataSchema.nullable().optional(),
    })
    .superRefine(validateDateOrder);

export type UpdateConsolidationRelationshipBody = z.infer<
    typeof updateConsolidationRelationshipBodySchema
>;

export const listConsolidationRelationshipsQuerySchema = z.object({
    entityUuid: z.string().uuid().optional(),
    type: z.nativeEnum(ConsolidationEntityRelationshipType).optional(),
    includeEnded: z
        .union([
            z.boolean(),
            z.enum(["true", "false"]).transform((value) => value === "true"),
        ])
        .optional(),
});

export type ListConsolidationRelationshipsQuery = z.infer<
    typeof listConsolidationRelationshipsQuerySchema
>;

const consolidationGroupBasisConfigSchema = z.object({
    basis: z.nativeEnum(AccountingLedgerBasis).optional(),
    ledgerIdsByEntityUuid: z
        .record(z.string().uuid(), z.string().uuid())
        .optional(),
});

function hasAtMostOneBasisSelection({
    basis,
    ledgerIdsByEntityUuid,
}: z.infer<typeof consolidationGroupBasisConfigSchema>): boolean {
    return basis === undefined || ledgerIdsByEntityUuid === undefined;
}

const exclusiveBasisSelectionRefinement = {
    message: "Choose either a basis or entity ledgers, not both",
};

export const consolidationGroupConfigSchema =
    consolidationGroupBasisConfigSchema.extend({
        periodConvention: z.string().optional(),
    });

export const consolidationGroupMemberSchema = z.object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    workspaceId: z.string().uuid(),
    entityUuid: z.string().uuid(),
    consolidationTreatment: z.nativeEnum(ConsolidationTreatment),
    joinedAt: z.string().nullable(),
});

export type ConsolidationGroupMemberResponse = z.infer<
    typeof consolidationGroupMemberSchema
>;

export const consolidationGroupSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    name: z.string(),
    parentEntityUuid: z.string().uuid(),
    status: z.nativeEnum(ConsolidationGroupStatus),
    overlayLedgerId: z.string().uuid().nullable(),
    config: consolidationGroupConfigSchema.nullable(),
    members: z.array(consolidationGroupMemberSchema),
});

export type ConsolidationGroupResponse = z.infer<
    typeof consolidationGroupSchema
>;

export const createConsolidationGroupBodySchema = z.object({
    name: z.string().trim().min(1).max(200),
    parentEntityUuid: z.string().uuid(),
    memberEntityUuids: z.array(z.string().uuid()).min(1),
    config: consolidationGroupBasisConfigSchema
        .refine(hasAtMostOneBasisSelection, exclusiveBasisSelectionRefinement)
        .optional(),
});

export type CreateConsolidationGroupBody = z.infer<
    typeof createConsolidationGroupBodySchema
>;

export const consolidationEligibilityBodySchema = z.object({
    parentEntityUuid: z.string().uuid(),
    memberEntityUuids: z.array(z.string().uuid()).min(1),
});

export type ConsolidationEligibilityBody = z.infer<
    typeof consolidationEligibilityBodySchema
>;

export enum ConsolidationEligibilityRule {
    notBusinessEntity = "not_business_entity",
    parentAlreadyGrouped = "parent_already_grouped",
    alreadyMember = "already_member",
    missingOwnershipEdge = "missing_ownership_edge",
    notFlatHierarchy = "not_flat_hierarchy",
    notFullOwnership = "not_full_ownership",
    basisMismatch = "basis_mismatch",
}

export const consolidationEligibilityViolationSchema = z.object({
    rule: z.nativeEnum(ConsolidationEligibilityRule),
    entityUuid: z.string().uuid().nullable(),
    message: z.string(),
});

export type ConsolidationEligibilityViolation = z.infer<
    typeof consolidationEligibilityViolationSchema
>;

/**
 * `not_full_ownership` is a soft violation: the group is still creatable and
 * the affected member is stored with `does_not_consolidate` (no NCI in V1).
 * All other rules are hard violations that make the group ineligible.
 */
export const consolidationEligibilityResultSchema = z.object({
    eligible: z.boolean(),
    basis: z.nativeEnum(AccountingLedgerBasis).nullable(),
    violations: z.array(consolidationEligibilityViolationSchema),
});

export type ConsolidationEligibilityResult = z.infer<
    typeof consolidationEligibilityResultSchema
>;

export const consolidationBasisOptionsBodySchema = z.object({
    entityUuids: z.array(z.string().uuid()).min(1),
});

export type ConsolidationBasisOptionsBody = z.infer<
    typeof consolidationBasisOptionsBodySchema
>;

export const consolidationBasisOptionsResultSchema = z.object({
    basisOptions: z.array(z.nativeEnum(AccountingLedgerBasis)),
    ledgerOptionsByEntityUuid: z.record(
        z.string().uuid(),
        z.array(
            z.object({
                id: z.string().uuid(),
                basis: z.nativeEnum(AccountingLedgerBasis),
                name: z.string(),
            }),
        ),
    ),
});

export type ConsolidationBasisOptionsResult = z.infer<
    typeof consolidationBasisOptionsResultSchema
>;

export const updateConsolidationGroupConfigBodySchema =
    consolidationGroupBasisConfigSchema
        .refine(
            ({ basis, ledgerIdsByEntityUuid }) =>
                basis !== undefined || ledgerIdsByEntityUuid !== undefined,
            { message: "A basis or entity ledger selection is required" },
        )
        .refine(hasAtMostOneBasisSelection, exclusiveBasisSelectionRefinement);

export type UpdateConsolidationGroupConfigBody = z.infer<
    typeof updateConsolidationGroupConfigBodySchema
>;

export const updateConsolidationGroupMemberBodySchema = z.object({
    consolidationTreatment: z.nativeEnum(ConsolidationTreatment),
});

export type UpdateConsolidationGroupMemberBody = z.infer<
    typeof updateConsolidationGroupMemberBodySchema
>;

export const canvasNodePositionSchema = z.object({
    x: z.number(),
    y: z.number(),
});

// Upper bound on how many node coordinates a single workspace can persist, so
// a misbehaving client cannot grow the jsonb blob without limit.
const MAX_CANVAS_LAYOUT_NODES = 500;

export const canvasLayoutPositionsSchema = z
    .record(z.string().min(1).max(200), canvasNodePositionSchema)
    .refine(
        (positions) => Object.keys(positions).length <= MAX_CANVAS_LAYOUT_NODES,
        { message: "Canvas layout has too many nodes" },
    );

export type CanvasLayoutPositions = z.infer<typeof canvasLayoutPositionsSchema>;

export const consolidationCanvasLayoutSchema = z.object({
    workspaceId: z.string().uuid(),
    positions: canvasLayoutPositionsSchema,
    // Null when the workspace has never saved a layout (ELK still drives it).
    updatedAt: z.string().nullable(),
});

export type ConsolidationCanvasLayoutResponse = z.infer<
    typeof consolidationCanvasLayoutSchema
>;

export const updateConsolidationCanvasLayoutBodySchema = z.object({
    positions: canvasLayoutPositionsSchema,
});

export type UpdateConsolidationCanvasLayoutBody = z.infer<
    typeof updateConsolidationCanvasLayoutBodySchema
>;
